import { Box, HStack, Text } from '@chakra-ui/react';
import { BaseIcon, BaseTable, BaseTableIconButton } from '@invara-dev/react-base';
import { faCrown, faSlash } from '@fortawesome/free-solid-svg-icons';
import { createColumnHelper, Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { scheduleType } from '../../features/workflow/types';
import { useWorkflowSocket } from '../../features/workflow/hub/useWorkflowSocket';
import { formatToTime } from '../../features/workflow/utils/formatter';

type Props = {
  hideActions?: boolean;
  hideSearch?: boolean;
  hideSort?: boolean;
};

export const ScheduleTable = (props: Props) => {
  const schedules = useWorkflowSocket((r) => r.data?.schedule);
  const invoke = useWorkflowSocket((r) => r.invoke);
  const data = useMemo(() => Object.entries(schedules ?? {}).map(([_, value]) => value), [schedules]);
  const columnHelper = createColumnHelper<scheduleType>();
  const columns = [
    columnHelper.accessor('Name', { header: 'Name' }),
    columnHelper.accessor('IsCoordinator', {
      header: 'Manager',
      cell(props) {
        return <>{props.row.original.IsCoordinator ? <BaseIcon icon={faCrown} /> : null}</>;
      }
    }),
    columnHelper.accessor('Shift.Start', {
      header: 'Shift Start',
      enableSorting: props.hideSort ? props.hideSort : true,
      sortingFn: sortTimeStrings,
      cell(props) {
        return <RenderSchedule timeString={props.row.original.Shift?.Start} />;
      }
    }),
    columnHelper.accessor('Shift.End', {
      header: 'Shift End',
      enableSorting: props.hideSort ? props.hideSort : true,
      sortingFn: sortTimeStrings,
      cell(props) {
        return <RenderSchedule timeString={props.row.original.Shift?.End} />;
      }
    }),
    columnHelper.accessor('Lunch.Start', {
      header: 'Break Start',
      enableSorting: props.hideSort ? props.hideSort : true,
      sortingFn: sortTimeStrings,
      cell(props) {
        return <RenderSchedule timeString={props.row.original.Lunch?.Start} />;
      }
    }),
    columnHelper.accessor('Lunch.End', {
      header: 'Break End',
      enableSorting: props.hideSort ? props.hideSort : true,
      sortingFn: sortTimeStrings,
      cell(props) {
        return <RenderSchedule timeString={props.row.original.Lunch?.End} />;
      }
    })
  ];
  return (
    <Box h='100%' minH={0} overflow='hidden'>
      <BaseTable
        tableHeight='100%'
        disableGlobalFilter={props.hideSearch ? props.hideSearch : false}
        hideActionHeader={props.hideActions ? props.hideActions : false}
        hideOptionsMenu
        actions={
          props.hideActions
            ? undefined
            : (row) => {
                return (
                  <HStack>
                    <BaseTableIconButton
                      onClick={() => {
                        if (!row.original.IsCoordinator) {
                          invoke('UpdateCoordinator', true, row.original.Id);
                        }
                        if (row.original.IsCoordinator) {
                          invoke('UpdateCoordinator', false, row.original.Id);
                        }
                      }}
                      toolTip={row.original.IsCoordinator ? 'Un-Set Coordinator' : 'Set Coordinator'}
                      icon={row.original.IsCoordinator ? faSlash : faCrown}
                    />
                  </HStack>
                );
              }
        }
        data={data}
        columns={columns}
      />
    </Box>
  );
};

const RenderSchedule = (props: { timeString: string | undefined | null }) => {
  const display = useMemo(() => formatToTime(props.timeString), [props.timeString]);
  return <Text>{display}</Text>;
};

const sortTimeStrings = (a: Row<scheduleType>, b: Row<scheduleType>, columnId: string) => {
  const aTime = dayjs(a.getValue(columnId));
  const bTime = dayjs(b.getValue(columnId));

  const now = dayjs();

  const aAfter = aTime.isSameOrAfter(now);
  const bAfter = bTime.isSameOrAfter(now);
  if (aAfter && bAfter) {
    return aTime.diff(bTime);
  }
  if (aAfter && bAfter) {
    return aTime.diff(bTime);
  }
  if (!aAfter && !bAfter) {
    return aTime.diff(bTime);
  }

  return aAfter ? -1 : 1;
};
